import React, { useEffect, useState } from 'react';
import { View, Text, StyleSheet, Pressable } from 'react-native';
import { CheckCircle2, Send } from 'lucide-react-native';
import { useTheme, SPACING, BORDER_RADIUS, TYPOGRAPHY } from '../../theme';
import { AdaptiveSheet, Button, ErrorBanner, Input } from '../common';
import { EVALUATION_STATUS_META, EVALUATION_STATUSES } from '../../constants/evaluationStatus';
import { EvaluationStatus } from '../../types';
import { useStructuresStore } from '../../store/structuresStore';
import { getErrorMessage } from '../../utils/errorHandler';

interface NotifyAgentSheetProps {
  open: boolean;
  structureId: string;
  currentStatus?: EvaluationStatus;
  onClose: () => void;
}

type Phase = 'idle' | 'sending' | 'sent' | 'error';

const NOTE_MAX_LENGTH = 500;

/**
 * "Notify my agent": the owner picks where they are on the checklist and
 * sends it to whoever has visibility into the property. Modeled on
 * SendReportSheet's confirm/pending/result phases, with an explicit `error`
 * phase (rather than folding the failure back into `idle`) so the sheet can
 * keep the picked status and typed note on screen while the banner shows
 * what went wrong.
 */
export function NotifyAgentSheet({
  open,
  structureId,
  currentStatus,
  onClose,
}: NotifyAgentSheetProps) {
  const { colors } = useTheme();
  const notifyAgent = useStructuresStore((s) => s.notifyAgent);

  const [phase, setPhase] = useState<Phase>('idle');
  const [status, setStatus] = useState<EvaluationStatus>(currentStatus ?? 'started');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [recipients, setRecipients] = useState<string[]>([]);

  // Reset per open, seeding the status pick from the claim's current value
  // (if any) rather than always starting at "Just getting started".
  useEffect(() => {
    if (open) {
      setPhase('idle');
      setStatus(currentStatus ?? 'started');
      setNote('');
      setError(null);
      setRecipients([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, structureId]);

  const handleSend = async () => {
    setPhase('sending');
    setError(null);
    try {
      const trimmed = note.trim();
      const result = await notifyAgent(structureId, status, trimmed || undefined);
      setRecipients(result.recipients);
      setPhase('sent');
    } catch (err) {
      setError(getErrorMessage(err));
      setPhase('error');
    }
  };

  const isSending = phase === 'sending';
  const isSent = phase === 'sent';

  return (
    <AdaptiveSheet isOpen={open} onRequestClose={onClose} onDismiss={onClose} dialogWidth={480}>
      <AdaptiveSheet.ScrollView testID="notify-agent-sheet" contentContainerStyle={styles.content}>
        {isSent ? (
          <>
            <View style={styles.resultHeader}>
              <CheckCircle2 size={32} color={colors.progress} />
              <Text style={[styles.title, { color: colors.text }]}>Update sent</Text>
            </View>
            {recipients.length > 0 ? (
              <>
                <Text style={[styles.body, { color: colors.textSecondary }]}>
                  {recipients.length === 1
                    ? 'Sent to 1 recipient.'
                    : `Sent to ${recipients.length} recipients.`}
                </Text>
                <View
                  style={[
                    styles.recipientsBox,
                    { backgroundColor: colors.surface, borderColor: colors.border },
                  ]}>
                  {recipients.map((email) => (
                    <Text key={email} style={[styles.recipient, { color: colors.textSecondary }]}>
                      {email}
                    </Text>
                  ))}
                </View>
              </>
            ) : (
              <Text style={[styles.body, { color: colors.textSecondary }]}>
                No agent is linked to this property yet. Add report recipients below to include them
                next time.
              </Text>
            )}
            <Button title="Done" onPress={onClose} fullWidth testID="notify-agent-done" />
          </>
        ) : (
          <>
            <View style={styles.resultHeader}>
              <Send size={28} color={colors.primary} />
              <Text style={[styles.title, { color: colors.text }]}>Notify my agent</Text>
            </View>
            <Text style={[styles.body, { color: colors.textSecondary }]}>
              Let your agent know where you are on the checklist.
            </Text>

            <View style={styles.optionList}>
              {EVALUATION_STATUSES.map((value) => {
                const meta = EVALUATION_STATUS_META[value];
                const selected = status === value;
                return (
                  <Pressable
                    key={value}
                    onPress={() => setStatus(value)}
                    testID={`notify-agent-status-${value}`}
                    accessibilityRole="radio"
                    accessibilityState={{ selected }}
                    // RN-web doesn't emit aria-* from accessibilityState —
                    // role=radio needs aria-checked in the DOM (see
                    // ClaimSheet's InsurerRow for the same pattern).
                    aria-checked={selected}
                    accessibilityLabel={`${meta.label} — ${meta.blurb}`}
                    style={[
                      styles.optionRow,
                      {
                        borderColor: selected ? colors.action : colors.border,
                        backgroundColor: selected ? colors.action : 'transparent',
                      },
                    ]}>
                    <Text
                      style={[
                        styles.optionLabel,
                        { color: selected ? colors.actionOn : colors.text },
                      ]}>
                      {meta.label}
                    </Text>
                    <Text
                      style={[
                        styles.optionBlurb,
                        { color: selected ? colors.actionOn : colors.textSecondary },
                      ]}>
                      {meta.blurb}
                    </Text>
                  </Pressable>
                );
              })}
            </View>

            <Input
              label="Note (optional)"
              value={note}
              onChangeText={(text) => setNote(text.slice(0, NOTE_MAX_LENGTH))}
              placeholder="Anything your agent should know"
              multiline
              numberOfLines={3}
              maxLength={NOTE_MAX_LENGTH}
              hint={`${note.length}/${NOTE_MAX_LENGTH}`}
              testID="notify-agent-note"
              style={styles.noteInput}
            />

            <ErrorBanner message={error} />

            <Button
              title={isSending ? 'Sending...' : 'Send update'}
              onPress={handleSend}
              loading={isSending}
              fullWidth
              size="lg"
              leftIcon={Send}
              testID="notify-agent-send"
            />
            <Button
              title="Cancel"
              onPress={onClose}
              variant="ghost"
              fullWidth
              disabled={isSending}
            />
          </>
        )}
        {/* Scroll sentinel per sheet conventions. */}
        <View testID="notify-agent-sheet-end" style={styles.scrollSentinel} />
      </AdaptiveSheet.ScrollView>
    </AdaptiveSheet>
  );
}

const styles = StyleSheet.create({
  content: {
    padding: SPACING.lg,
    gap: SPACING.smd,
  },
  resultHeader: {
    alignItems: 'center',
    gap: SPACING.sm,
  },
  title: {
    ...TYPOGRAPHY.h4,
  },
  body: {
    ...TYPOGRAPHY.body,
  },
  optionList: {
    gap: SPACING.sm,
  },
  optionRow: {
    borderWidth: 1,
    borderRadius: BORDER_RADIUS.md,
    padding: SPACING.smd,
    gap: 2,
  },
  optionLabel: {
    ...TYPOGRAPHY.label,
  },
  optionBlurb: {
    ...TYPOGRAPHY.bodySmall,
  },
  noteInput: {
    minHeight: 72,
    textAlignVertical: 'top',
  },
  recipientsBox: {
    borderRadius: BORDER_RADIUS.md,
    borderWidth: 1,
    padding: SPACING.md,
    gap: SPACING.xs,
  },
  recipient: {
    ...TYPOGRAPHY.bodySmall,
  },
  scrollSentinel: {
    height: 1,
  },
});
